import { Component } from 'react';
import { connect } from 'react-redux';
import { debounce } from 'lodash';
import { Toast } from 'primereact/toast';
import { getBranchesLatest } from '../../../../store/actions';
import Authentication from '../../../session';
import withRouter from '../../../lib/withRouter';
import LoadingComponent from '../../../loadingComponent';
import { INITIAL_STATE, SEARCH_TYPE_OPTIONS, TOAST_TYPES } from './constants';
import { AdmissionCategoryChangeService } from './services';
import { AdmissionCategoryChangeUtils } from './utils';
import { FeeHandlers } from './utils/feeHandlers';
import SearchFilters from './components/SearchFilters';
import StudentsDataTable from './components/StudentsDataTable';
import AdmissionChangeRequestDialog from './components/AdmissionChangeRequestDialog';
import ApprovalRequestsDialog from './components/ApprovalRequestsDialog';
import OldFeeInfoDialog from './components/OldFeeInfoDialog';
import NewFeeInfoDialog from './components/NewFeeInfoDialog';
import './styles.scss';

class AdmissionCategoryChange extends Component {
    constructor(props) {
        super(props);
        this.state = { ...INITIAL_STATE };
        this.debouncedApiCall = debounce(this.filterUsersData, 500);
        this.service = new AdmissionCategoryChangeService();
        this.feeHandlers = new FeeHandlers(this);
    }

    // Lifecycle Methods
    componentDidMount() {
        if (this.state.dropdownsData?.sectionId) {
            this.filterUsersData();
        }
        this.getAdmissionCategoryOptions();
    }

    // Utility Methods
    updateState = (updates, callback) => {
        this.setState(updates, callback);
    };

    showToast = (severity, summary, detail = '', life = 3000) => {
        this.toast?.show({ severity, summary, detail, life });
    };

    isColumnVisible = (id) => {
        return this.state.columns.find(c => c.id === id)?.isVisible || false;
    };

    // API Methods
    filterUsersData = async () => {
        this.updateState({ isLoading: true, users: [] });

        const { lazyParams, globalSearch, dropdownsData, searchLevel, searchType, searchValue } = this.state;
        
        const result = await this.service.filterUsersData(
            lazyParams, 
            globalSearch, 
            dropdownsData, 
            searchLevel, 
            searchType, 
            searchValue
        );

        if (result.success) {
            this.updateState({
                isLoading: false,
                users: result.users,
                totalRecords: result.totalRecords,
                isUserNameSearch: false,
                isUserEnrollSearch: false,
                searchValue: ''
            });
        } else {
            this.updateState({ isLoading: false });
            this.showToast(TOAST_TYPES.ERROR, 'Some error occurred', result.error);
        }
    };

    getAdmissionCategoryOptions = async () => {
        const result = await this.service.getAdmissionCategoryOptions();
        
        if (result.success) {
            this.updateState({ admissionCategories: result.data });
        } else {
            this.showToast(TOAST_TYPES.ERROR, 'Error', result.error);
        }
    };

    checkAdmissionChangeEligibility = async () => {
        if (!this.validateChangeRequest()) return;

        this.updateState({ isLoading: true });
        
        const result = await this.service.checkAdmissionChangeEligibility(
            this.state.selectedStudentData, 
            this.state.effectiveDate
        );

        this.updateState({ isLoading: false });

        if (result.success) {
            if (result.data && result.data.length !== 0) {
                this.updateState({
                    admissionChangeCheckResponse: result.data,
                    message: result.message,
                    showResponseDialog: true,
                    isRaiseDialogOpen: false
                });
            } else {
                this.updateState({
                    admissionChangeCheckResponse: [],
                    message: result.message,
                    isRaiseDialogOpen: false,
                    showResponseDialog: false,
                }, () => this.getAdmissionChangeFeeDetails());
            }
        } else {
            this.showToast(TOAST_TYPES.ERROR, 'Error', result.error);
        }
    };

    getAdmissionChangeFeeDetails = async () => {
        const result = await this.service.getAdmissionChangeFeeDetails(
            this.state.selectedStudentData,
            this.state.selectedAdmission,
            this.state.effectiveDate
        );

        if (result.success) {
            if (result.hasOldFee) {
                this.updateState({
                    oldFeeInfo: result.oldFeeInfo,
                    newFeeResponse: result.newFeeResponse,
                    manualAdjustmentFeeInfo: result.manualAdjustmentFeeInfo,
                    showResponseDialog: false,
                    onShowOldFeeInfo: true,
                    totalClosingDueAmt: result.totalClosingDueAmt,
                    totalClosingAdvance: result.totalClosingAdvance,
                    adjustmentRuleNo: result.adjustmentRuleNo,
                });
            } else {
                // Show success message if provided
                if (result.showSuccessMessage && result.successMessage) {
                    this.showToast(TOAST_TYPES.SUCCESS, 'Success', result.successMessage);
                }
                
                this.updateState({
                    oldFeeInfo: [],
                    adjustmentRuleNo: result.adjustmentRuleNo,
                    newFeeResponse: result.newFeeResponse,
                    manualAdjustmentFeeInfo: result.manualAdjustmentFeeInfo,
                    showResponseDialog: false,
                    onShowOldFeeInfo: false,
                    onShowNewFeeInfo: true,
                }, () => this.feeHandlers.handleFeePlanChange());
            }
        } else {
            this.resetDialogState();
            this.showToast(TOAST_TYPES.ERROR, 'Error', result.error);
        }
    };

    raiseChangeRequest = async (newFeeData) => {
        if (!this.validateChangeRequest()) return;

        try {
            const categoryName = this.state.admissionCategories
                .find(cat => cat._id === this.state.selectedAdmission)?.categoryName || '';

            const cleanedStudentData = await AdmissionCategoryChangeUtils.removeOuterDuplicates(
                this.state.selectedStudentData, 
                'user_info'
            );

            const payload = {
                ...cleanedStudentData,
                admissionRule: this.state.adjustmentRuleNo,
                academicYear: localStorage.getItem('userAcademicYear'),
                admissionChangeRequest: 'PENDING',
                requestedAdmissionCat: this.state.selectedAdmission,
                requestedAdmissionName: categoryName,
                requestRaisedBy: localStorage.getItem('userId'),
                requestRaisedByName: localStorage.getItem('username'),
                effectiveDate: this.state.effectiveDate,
                remarks: this.state.remarks,
                newFeeData: newFeeData,
                oldFeeInfo: this.state.oldFeeInfo?.feeInfo || [],
            };
            
            delete payload._id;

            this.updateState({ isLoading: true });
            
            const result = await this.service.raiseChangeRequest(payload);
            
            if (result.success) {
                this.updateState({
                    selectedAdmission: '',
                    isLoading: false,
                    isRaiseDialogOpen: false,
                    onShowOldFeeInfo: false,
                    onShowNewFeeInfo: false,
                    isAddEnabled: false,
                    effectiveDate: '',
                    remarks: "",
                    selectedFeeSchedule: null,
                    relatedFeeSchedules: []
                }, this.filterUsersData);
                
                this.showToast(TOAST_TYPES.SUCCESS, result.message);
            } else {
                this.updateState({ 
                    isLoading: false,
                    selectedFeeSchedule: null,
                    relatedFeeSchedules: []
                });
                this.showToast(TOAST_TYPES.ERROR, 'Some error occurred', result.error);
            }
        } catch (error) {
            this.updateState({ 
                isLoading: false,
                selectedFeeSchedule: null,
                relatedFeeSchedules: []
            });
            this.showToast(TOAST_TYPES.ERROR, 'Some error occurred', error.message);
        }
    };

    fetchRelatedFeeSchedules = async () => {
        try {
            this.updateState({ isLoading: true });
            
            const result = await this.service.fetchRelatedFeeSchedules(
                this.state.selectedStudentData, 
                this.state.newFeeResponse
            );
            
            if (result.success) {
                this.updateState({ 
                    relatedFeeSchedules: result.data, 
                    isLoading: false 
                });
                this.showToast(TOAST_TYPES.SUCCESS, 'Success', 'No matching fee types. Select an alternative fee schedule.');
            } else {
                this.updateState({ 
                    isLoading: false,
                    selectedFeeSchedule: null
                });
                this.showToast(TOAST_TYPES.ERROR, 'Error', result.error);
            }
        } catch (error) {
            this.updateState({ 
                isLoading: false,
                selectedFeeSchedule: null
            });
            this.showToast(TOAST_TYPES.ERROR, 'Error', error.message || 'An error occurred while fetching fee schedules');
        }
    };

    getFeeStructure = async () => {
        if (this.state.selectedStudentData?.branchId) {
            this.updateState({ isLoading: true });
            
            const result = await this.service.getFeeStructure(this.state.selectedStudentData?.branchId);
            
            if (result.success) {
                // Transform data to add feeTypeId field to each feeType
                const transformedData = Array.isArray(result.data) 
                    ? result.data.map(category => ({
                        ...category,
                        feeTypes: Array.isArray(category.feeTypes)
                            ? category.feeTypes.map(feeType => ({
                                ...feeType,
                                feeTypeId: feeType.feeTypeId || feeType._id  // Ensure feeTypeId exists
                            }))
                            : []
                    }))
                    : [];

                this.updateState({
                    feeStructureInfo: transformedData,
                    isLoading: false
                });
            } else {
                this.showToast(TOAST_TYPES.ERROR, 'Error', result.error);
                this.updateState({ feeStructureInfo: [], isLoading: false });
            }
        }
    };

    getInstallmentsList = async () => {
        this.updateState({ isLoading: true });
        
        if (!this.state.selectedStudentData?.branchId) {
            this.updateState({ isLoading: false });
            return;
        }

        const result = await this.service.getInstallmentsList(this.state.selectedStudentData?.branchId);
        
        if (result.success) {
            this.updateState({ plansData: result.data, isLoading: false });
        } else {
            this.updateState({ isLoading: false });
            this.showToast(TOAST_TYPES.ERROR, 'Error', result.error);
        }
    };

    // Validation Methods
    validateChangeRequest = () => {
        const { selectedAdmission, effectiveDate, remarks } = this.state;
        
        if (!selectedAdmission) {
            this.showToast(TOAST_TYPES.ERROR, 'Please Select Admission Category');
            return false;
        }
        
        if (!effectiveDate) {
            this.showToast(TOAST_TYPES.ERROR, 'Please Select Effective Date');
            return false;
        }
        
        if (!remarks) {
            this.showToast(TOAST_TYPES.ERROR, 'Please Enter Reason for Change');
            return false;
        }
        
        return true;
    };

    // Event Handlers
    onPage = (event) => {
        this.updateState({
            lazyParams: { ...this.state.lazyParams, ...event }
        }, this.filterUsersData);
    };

    onSort = (event) => {
        this.updateState({
            lazyParams: { ...this.state.lazyParams, ...event }
        }, this.filterUsersData);
    };

    onFilter = (event) => {
        event.first = 0;
        this.updateState({
            lazyParams: {
                ...this.state.lazyParams,
                filters: event.filters
            }
        }, this.filterUsersData);
    };

    setDropdownValues = (dropdownsData, payload) => {
        this.updateState({
            dropdownsData,
            studentIds: payload?.studentIds || [],
            enrollmentIds: payload?.enrollmentIds || [],
        }, this.filterUsersData);
    };

    handleSearchLevel = (value) => {
        this.updateState({ searchLevel: value });
    };

    onChangeAdmissionCategory = (value) => {
        if (value === this.state.selectedStudentData?.admissionCategory) {
            this.showToast(TOAST_TYPES.ERROR, 'Student Already in this Admission');
        } else {
            this.updateState({ selectedAdmission: value });
        }
    };

    // Dialog State Management
    resetDialogState = () => {
        this.updateState({
            isRaiseDialogOpen: false,
            selectedAdmission: '',
            remarks: '',
            effectiveDate: '',
            onShowOldFeeInfo: false,
            onShowNewFeeInfo: false,
            showResponseDialog: false,
            selectedFeeSchedule: null,
            relatedFeeSchedules: [],
            isShowInstallmentInfo: false,
            isAddEnabled: false
        });
    };

    handleRequestSubmit = () => {
        if (this.validateChangeRequest()) {
            this.checkAdmissionChangeEligibility();
        } else {
            this.showToast(TOAST_TYPES.ERROR, 'Error', 'Please fill all the fields');
        }
    };

    handleRaiseRequest = (rowData) => {
        this.updateState({
            isRaiseDialogOpen: true,
            selectedStudentData: rowData
        });
    };

    handleResponseDialogClose = () => {
        this.updateState({ 
            showResponseDialog: false, 
            admissionChangeCheckResponse: null, 
            message: '', 
            selectedAdmission: '', 
            remarks: '', 
            effectiveDate: '',
            selectedFeeSchedule: null,
            relatedFeeSchedules: []
        });
    };

    handleOldFeeDialogClose = () => {
        this.updateState({ 
            showResponseDialog: false, 
            admissionChangeCheckResponse: null, 
            message: '', 
            onShowOldFeeInfo: false, 
            selectedAdmission: '', 
            remarks: '', 
            effectiveDate: '',
            selectedFeeSchedule: null,
            relatedFeeSchedules: [],
            isShowInstallmentInfo: false,
            isAddEnabled: false
        });
    };

    handleNewFeeDialogClose = () => {
        this.updateState({ 
            onShowNewFeeInfo: false, 
            selectedAdmission: '', 
            remarks: '', 
            effectiveDate: '',
            selectedFeeSchedule: null,
            relatedFeeSchedules: [],
            isShowInstallmentInfo: false,
            isAddEnabled: false
        });
    };

    handleBackToOldFee = () => {
        this.updateState({
            onShowOldFeeInfo: false,
            isRaiseDialogOpen: true,
            relatedFeeSchedules: [],
            selectedFeeSchedule: null,
            isShowInstallmentInfo: false,
            isAddEnabled: false
        });
    };

    handleBackToNewFee = () => {
        this.updateState({
            onShowOldFeeInfo: this.state.oldFeeInfo?.length ? true : false,
            onShowNewFeeInfo: false,
            isAddEnabled: false,
            isRaiseDialogOpen: !this.state.oldFeeInfo?.length ? true : false,
            relatedFeeSchedules: [],
            selectedFeeSchedule: null,
            isShowInstallmentInfo: false
        });
    };

    handleNewFeeTypesClick = () => {
        this.updateState({
            showResponseDialog: false,
            onShowOldFeeInfo: false,
            isAddEnabled: false
        }, () => this.feeHandlers.handleFeePlanChange());
    };

    handleNextClick = () => {
        this.updateState({
            showResponseDialog: false,
            onShowOldFeeInfo: false,
            onShowNewFeeInfo: true
        }, () => this.feeHandlers.onhandleFee());
    };

    handleFeeScheduleChange = (e) => {
        this.updateState({ 
            selectedFeeSchedule: e.value, 
            isActiveAlterFeeSchedules: true, 
            isAddEnabled: false 
        });
        this.feeHandlers.handleSelectedFeeScheduleChange(e.value);
    };

    handleAddClick = () => {
        // If installmentData is empty, populate it from selectedFeePlan
        if (!this.state.installmentData?.length && this.state.selectedFeePlan) {
            const { selectedFeePlan, selectedFeeStructure } = this.state;
            
            // Get all non-one-time fee types from selectedFeeStructure
            const regularFees = [];
            if (Array.isArray(selectedFeeStructure)) {
                selectedFeeStructure.forEach(category => {
                    if (Array.isArray(category.feeTypes)) {
                        category.feeTypes.forEach(feeType => {
                            if (!feeType.isOneTime) {
                                regularFees.push(feeType);
                            }
                        });
                    }
                });
            }
            
            // Create installmentData from all installments (without date restriction)
            const installmentData = selectedFeePlan.installments?.map((installment, index) => ({
                installment: `Installment ${index + 1}`,
                installmentId: installment.installmentId,
                dueDate: new Date(installment.date || Date.now()),
                feeTypes: regularFees.map(feeType => ({
                    ...feeType,
                    percentage: 0,
                    calculatedFee: 0
                }))
            })) || [];
            
            this.updateState({ 
                isShowInstallmentInfo: true, 
                isAddEnabled: true,
                installmentData: installmentData
            });
        } else {
            this.updateState({ 
                isShowInstallmentInfo: true, 
                isAddEnabled: true 
            });
        }
    };

    render() {
        const oldFeeInfo = this.state.oldFeeInfo?.feeInfo || [];
        const totalClosingDueAmt = oldFeeInfo.reduce((sum, item) => sum + (item.closingDueAmt || 0), 0);
        const totalClosingAdvance = oldFeeInfo.reduce((sum, item) => sum + (item.closingAdvance || 0), 0);

        return (
            <div>
                <SearchFilters
                    searchLevel={this.state.searchLevel}
                    onSearchLevelChange={this.handleSearchLevel}
                    setDropdownValues={this.setDropdownValues}
                    studentSearchRef={this.studentSearchRef}
                />

                <StudentsDataTable
                    users={this.state.users}
                    admissionCategories={this.state.admissionCategories}
                    lazyParams={this.state.lazyParams}
                    totalRecords={this.state.totalRecords}
                    onPage={this.onPage}
                    onSort={this.onSort}
                    onFilter={this.onFilter}
                    onRaiseRequest={this.handleRaiseRequest}
                />

                <AdmissionChangeRequestDialog
                    visible={this.state.isRaiseDialogOpen}
                    onHide={this.resetDialogState}
                    selectedStudentData={this.state.selectedStudentData}
                    admissionCategories={this.state.admissionCategories}
                    selectedAdmission={this.state.selectedAdmission}
                    onAdmissionChange={this.onChangeAdmissionCategory}
                    effectiveDate={this.state.effectiveDate}
                    onEffectiveDateChange={(value) => this.updateState({ effectiveDate: value })}
                    remarks={this.state.remarks}
                    onRemarksChange={(value) => this.updateState({ remarks: value })}
                    onSubmit={this.handleRequestSubmit}
                    onCancel={this.resetDialogState}
                />

                <ApprovalRequestsDialog
                    visible={this.state.showResponseDialog}
                    onHide={this.handleResponseDialogClose}
                    admissionChangeCheckResponse={this.state.admissionChangeCheckResponse}
                    message={this.state.message}
                    onCancel={this.handleResponseDialogClose}
                />

                <OldFeeInfoDialog
                    visible={this.state?.oldFeeInfo && this.state.onShowOldFeeInfo}
                    onHide={this.handleOldFeeDialogClose}
                    oldFeeInfo={this.state.oldFeeInfo}
                    adjustmentRuleNo={this.state.adjustmentRuleNo}
                    totalClosingDueAmt={totalClosingDueAmt}
                    totalClosingAdvance={totalClosingAdvance}
                    onClosingAmountChange={this.feeHandlers.handleClosingAmountChange}
                    onBackClick={this.handleBackToOldFee}
                    onNewFeeTypesClick={this.handleNewFeeTypesClick}
                    onCancel={this.handleOldFeeDialogClose}
                />

                <NewFeeInfoDialog
                    visible={this.state.onShowNewFeeInfo}
                    onHide={this.handleNewFeeDialogClose}
                    isFromFeeScheduleSelection={this.state.isFromFeeScheduleSelection}
                    newFeeResponse={this.state.newFeeResponse}
                    relatedFeeSchedules={this.state.relatedFeeSchedules}
                    selectedFeeSchedule={this.state.selectedFeeSchedule}
                    onFeeScheduleChange={this.handleFeeScheduleChange}
                    selectedFeeStructure={this.state.selectedFeeStructure}
                    isAddEnabled={this.state.isAddEnabled}
                    onAddClick={this.handleAddClick}
                    onTotalFeeChange={this.feeHandlers.handleTotalFeeChange}
                    installmentData={this.state.installmentData}
                    oneTimeFees={this.state.oneTimeFees}
                    isShowInstallmentInfo={this.state.isShowInstallmentInfo}
                    onOneTimeFeeChange={this.feeHandlers.handleOneTimeFeeChange}
                    onOneTimeDueDateChange={this.feeHandlers.handleOneTimeDueDateChange}
                    selectedFeePlan={this.state.selectedFeePlan}
                    onPercentageChange={this.feeHandlers.handlePercentageChange}
                    onCalculatedFeeChange={this.feeHandlers.handleCalculatedFeeChange}
                    onBackClick={this.handleBackToNewFee}
                    onNext={this.handleNextClick}
                    onCancel={this.handleNewFeeDialogClose}
                />

                <Toast ref={(el) => (this.toast = el)} position="bottom-right" />
                {this.state.isLoading && <LoadingComponent />}
            </div>
        );
    }
}

const mapStatesToProps = (state) => ({
    branchData:
        (state.branchDataLatest &&
            state.branchDataLatest.data &&
            state.branchDataLatest.data.data.filter((each) => {
                if (each.level == 1) {
                    return { ...each };
                }
            })) ||
        []
});

export default connect(mapStatesToProps, {
    getBranchesLatest
})(Authentication(withRouter(AdmissionCategoryChange)));
